import React, {useCallback, useEffect, useMemo, useState} from "react";
import {DataTable} from "@/lib/components/DataTable";
import {
    Checkbox,
    FormControl,
    Grid2,
    InputAdornment,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    TablePagination,
    TextField
} from "@mui/material";
import {Close} from "@mui/icons-material";
import {IconButtonEx} from "@/lib/components/buttons/IconButtonEx";
import {CartonGradeCatalogDTO, CartonGradePlantDisplayDTO} from "@/api/data-contracts";
import {getI18n, useTranslation} from "react-i18next";
import {CommonI18N, RequestI18N} from "@/translations";
import {usePlants} from "@/datacaches";
import {booleanField} from "@/common/fields/boolean-field";
import {formatNumber} from "@/lib/utils";
import {EDGE_CRUSH_UNIT, GSM} from "@/constants";
import {OrderDataType} from "@/model/orderData";
import {color_orange} from "@/agentur/AgenturColors";
import {Layout, LayoutItem} from "@/lib/components/Layout";
import {ColumnDef} from "@tanstack/react-table";
import {useTheme} from "@mui/material/styles";

export interface CartonGradeSelectorTableProps {
    grades: CartonGradeCatalogDTO[];
    initialFilter?: string;
    mode: OrderDataType;
    selectedLocations?: string[];
    onLocationsChange?: (locations: string[]) => void;
    originalGrade?: CartonGradeCatalogDTO;
    selectedGrade?: CartonGradeCatalogDTO;
    onSelect: (grade: CartonGradeCatalogDTO) => void;
    setSelectedId: (id: number | null) => void;
    selectedId?: number | null;
}

export const CartonGradeSelectorTable = ({
                                             grades,
                                             initialFilter,
                                             mode,
                                             selectedLocations,
                                             onLocationsChange,
                                             onSelect,
                                             originalGrade,
                                             selectedGrade,
                                             setSelectedId,
                                             selectedId
                                         }: CartonGradeSelectorTableProps) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const locale = getI18n().language;
    const [filter, setFilter] = useState(initialFilter);
    const [internalSelectedLocations, setInternalSelectedLocations] = useState<string[] | undefined>(selectedLocations);
    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);

    const [plants] = usePlants();

    const plantData = useMemo(() => {
        const locationMap = new Map<string, CartonGradePlantDisplayDTO[]>();
        for (const plant of plants) {
            if (plant.location) {
                if (!locationMap.has(plant.location)) {
                    locationMap.set(plant.location, []);
                }
                const locationPlants = locationMap.get(plant.location)!;
                const plantDisplay: CartonGradePlantDisplayDTO = {
                    id: plant.id,
                    location: plant.location,
                    name: plant.name,
                    materialProductionPriorityCode: "A"
                };
                if (!locationPlants.some((p) => p.name === plant.name)) {
                    locationPlants.push(plantDisplay);
                }
            }
        }
        const locations = Array.from(locationMap.keys()).sort((a, b) => a.localeCompare(b));
        const locationToPlantsMap = new Map<string, CartonGradePlantDisplayDTO[]>();
        for (const [location, plants] of locationMap) {
            locationToPlantsMap.set(location, plants);
        }
        return {
            locations,
            locationToPlantsMap
        };
    }, [plants]);

    useEffect(() => {
        setInternalSelectedLocations(selectedLocations);
    }, [selectedLocations, selectedGrade]);

    useEffect(() => {
        if (onLocationsChange && internalSelectedLocations && mode === "quote") {
            onLocationsChange(internalSelectedLocations);
        }
    }, [mode, internalSelectedLocations, onLocationsChange]);

    //die Grades können zwei Leerzeichen haben, daher trimmen und alle mehrfachen Leerzeichen durch ein einzelnes ersetzen
    const normalizeGradeCode = (str: string | undefined | null): string =>
            (str ?? "").trim().replace(/\s+/g, " ").toLowerCase();

    const filteredGrades = useMemo(() => {
        let result = grades;
        if (filter?.trim()) {
            const filterValue = normalizeGradeCode(filter);
            result = grades.filter((g) => {
                const code = normalizeGradeCode(g.values?.code);
                return code.includes(filterValue);
            });
        }

        if (internalSelectedLocations && internalSelectedLocations.length > 0) {
            result = result.filter((grade) => {
                if (!grade.plants || grade.plants.length === 0) {
                    return false;
                }
                return grade.plants.some((plant) => internalSelectedLocations?.includes(plant.location || ""));
            });
        }

        return originalGrade ? [originalGrade, ...result] : result;
    }, [grades, filter, internalSelectedLocations, originalGrade]);

    const paginatedGrades = useMemo(
            () => filteredGrades.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
            [filteredGrades, page, rowsPerPage]
    );

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(Number.parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRowDoubleClick = useCallback(
            (row: CartonGradeCatalogDTO) => {
                setSelectedId(row.id!);
                const sel = filteredGrades.find((g) => g.id === row.id);
                if (sel) {
                    onSelect(sel);
                }
            },
            [filteredGrades, onSelect, setSelectedId]
    );

    const columns: ColumnDef<CartonGradeCatalogDTO, any>[] = [
        {
            header: t(RequestI18N.grade.dialog.qualityGradeHeader),
            accessorKey: "grade",
            cell: (info: any) => info.row.original.values?.code ?? ""
        },
        {
            header: t(RequestI18N.grade.form.fluteLabel),
            accessorKey: "flute",
            cell: (info: any) => info.row.original.values?.flute ?? ""
        },
        {
            header: t(RequestI18N.grade.table.adOuterLiner),
            accessorKey: "outerLiner",
            cell: (info: any) => info.row.original.values?.outerLiner ?? ""
        },
        {
            header: t(RequestI18N.grade.table.w1Flute),
            accessorKey: "fluteOne",
            cell: (info: any) => info.row.original.values?.fluteOne ?? ""
        },
        {
            header: t(RequestI18N.grade.table.zd1Liner),
            accessorKey: "linerOne",
            cell: (info: any) => info.row.original.values?.linerOne ?? ""
        },
        {
            header: t(RequestI18N.grade.table.w2Flute),
            accessorKey: "fluteTwo",
            cell: (info: any) => info.row.original.values?.fluteTwo ?? ""
        },
        {
            header: t(RequestI18N.grade.table.zd2Liner),
            accessorKey: "linerTwo",
            cell: (info: any) => info.row.original.values?.linerTwo ?? ""
        },
        {
            header: t(RequestI18N.grade.table.w3Flute),
            accessorKey: "fluteThree",
            cell: (info: any) => info.row.original.values?.fluteThree ?? ""
        },
        {
            header: t(RequestI18N.grade.table.idInnerLiner),
            accessorKey: "innerLiner",
            cell: (info: any) => info.row.original.values?.innerLiner ?? ""
        },
        {
            header: t(RequestI18N.grade.dialog.weightHeader),
            accessorKey: "weight",
            cell: (info: any) => `${formatNumber(info.row.original.values?.weight, 2, locale) ?? "-"} ${GSM}`
        },
        {
            header: t(RequestI18N.grade.table.ectEdgeCrush),
            accessorKey: "edgeCrushTest",
            cell: (info: any) =>
                    `${formatNumber(info.row.original.values?.edgeCrushTest, 2, locale) ?? "-"} ${EDGE_CRUSH_UNIT}`
        },
        {
            header: t(RequestI18N.grade.table.nf),
            accessorKey: "wetStrength",
            cell: (info: any) =>
                    booleanField(
                            info.row.original.values?.wetStrength,
                            t(CommonI18N.message.yes),
                            t(CommonI18N.message.no),
                            t(RequestI18N.grade.table.nf)
                    ).value
        },
        /*{
            header: t(RequestI18N.grade.dialog.prowellWorksHeader),
            accessorKey: "plants",
            cell: (info: any) =>
                info.row.original.plants?.map((p: CartonGradePlantDisplayDTO) => p.name).join(", ") ?? ""
        }*/
        {
            header: `${t(RequestI18N.grade.dialog.prowellWorksHeader)} (${t(RequestI18N.grade.dialog.standardGrade)})`,
            accessorKey: "standardPlants",
            cell: (info: any) => {
                const plants = info.row.original.plants ?? [];
                const standardPlants = plants
                        .filter((p: CartonGradePlantDisplayDTO) =>
                                ["A", "B", "C"].includes(p.materialProductionPriorityCode ?? "")
                        )
                        .map((p) => p.name)
                        .join(", ");
                return standardPlants || "-";
            }
        },
        {
            header: `${t(RequestI18N.grade.dialog.prowellWorksHeader)} (${t(RequestI18N.grade.dialog.specialGrade)})`,
            accessorKey: "specialPlants",
            cell: (info: any) => {
                const plants = info.row.original.plants ?? [];
                const specialPlants = plants
                        .filter((p: CartonGradePlantDisplayDTO) => p.materialProductionPriorityCode === "S")
                        .map((p) => p.name)
                        .join(", ");
                return specialPlants || "-";
            }
        }
    ];

    const firstRowFix = originalGrade
            ? {
                "& tbody>tr:first-child td": {
                    fontWeight: "bold",
                    // fontSize: "102%",
                    backgroundColor: theme.palette.mode === "dark" ? color_orange[1] : color_orange[2],
                    color: theme.palette.common.black,
                    position: "sticky",
                    top: 41
                    // borderTop: "4px solid #333",
                    // borderBottom: "4px solid #333"
                }
            }
            : {};

    return (
            <Layout root>
                <LayoutItem sx={{paddingBottom: 2}}>
                    <Grid2 container direction="row" spacing={2} width="100%">
                        <Grid2 size={{xs: 6, md: 3}}>
                            <TextField
                                    label={t(RequestI18N.grade.dialog.qualityGradeSearch)}
                                    value={filter}
                                    onChange={(e) => {
                                        setFilter(e.target.value);
                                        setPage(0);
                                        setSelectedId(null);
                                    }}
                                    fullWidth
                                    variant="outlined"
                                    margin="dense"
                                    size="small"
                                    slotProps={{
                                        input: {
                                            endAdornment: filter ? (
                                                    <InputAdornment position="end">
                                                        <IconButtonEx
                                                                Icon={Close}
                                                                onClick={() => {
                                                                    setFilter("");
                                                                    setSelectedId(null);
                                                                }}
                                                                tooltip={t(CommonI18N.action.clear)}
                                                        />
                                                    </InputAdornment>
                                            ) : null
                                        }
                                    }}
                            />
                        </Grid2>
                        <Grid2 size={{xs: 6, md: 6}}>
                            {
                                <FormControl fullWidth margin="dense" variant="outlined" size="small">
                                    <InputLabel id="plant-filter-label">
                                        {t(RequestI18N.grade.dialog.prowellWorksFilter)}
                                    </InputLabel>
                                    <Select
                                            labelId="plant-filter-label"
                                            multiple
                                            size="small"
                                            value={internalSelectedLocations}
                                            onChange={(e) => {
                                                const newLocations =
                                                        typeof e.target.value === "string"
                                                                ? e.target.value.split(",")
                                                                : e.target.value;
                                                setInternalSelectedLocations(newLocations);
                                                setSelectedId(null);
                                            }}
                                            input={<OutlinedInput
                                                    label={t(RequestI18N.grade.dialog.prowellWorksFilter)}/>}
                                            renderValue={(selected) => selected.join("x ")}
                                            MenuProps={{
                                                PaperProps: {
                                                    style: {
                                                        maxHeight: 300
                                                    }
                                                }
                                            }}
                                            data-cy="plant-filter"
                                    >
                                        {plantData.locations.map((location) => {
                                            const plants = plantData.locationToPlantsMap.get(location) || [];
                                            const plantNames = plants
                                                    .map((p) => p.name)
                                                    .filter(Boolean)
                                                    .join(", ");
                                            return (
                                                    <MenuItem key={location} value={location}>
                                                        <Checkbox
                                                                checked={internalSelectedLocations?.includes(location)}/>
                                                        <ListItemText primary={`${plantNames} ${location}`}/>
                                                    </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                            }
                        </Grid2>
                        <Grid2>
                            <TablePagination
                                    component="div"
                                    count={filteredGrades.length}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                            />
                        </Grid2>
                    </Grid2>
                </LayoutItem>
                <LayoutItem body>
                    <DataTable
                            columns={columns}
                            data={paginatedGrades}
                            noResultsValue={t(RequestI18N.grade.dialog.noGradesFound)}
                            selectedRowId={selectedId}
                            onRowClick={(row) => setSelectedId(row.id!)}
                            onRowDoubleClick={handleRowDoubleClick}
                            striped
                            isRowSelectable={() => true}
                            sx={{
                                height: "calc(100% - 52px)", // Adjust height to accommodate pagination
                                "& table": {
                                    width: "100%",
                                    tableLayout: "fixed",
                                    paddingBottom: 28
                                },
                                ...firstRowFix,
                                "& td, & th": {
                                    whiteSpace: "normal !important", // <-- Zeilenumbruch erzwingen
                                    overflowWrap: "break-word",
                                    wordBreak: "keep-all", // verhindert mitten-im-Wort-Trennung
                                    padding: "4px 8px", // weniger Abstand (oben/unten 4px, links/rechts 8px)
                                    margin: 0 // sicherheitshalber alle Margins weg
                                },
                                // HEADERS: dürfen mehrzeilig sein
                                "& thead th, & thead th *": {
                                    whiteSpace: "normal",
                                    wordBreak: "keep-all",
                                    overflowWrap: "normal",
                                    hyphens: "none",
                                    lineHeight: 1.1
                                },

                                // BODY-CELLS: ALLES auf Umbruch trimmen (span/div/Typographies etc.)
                                "& tbody td, & tbody td *": {
                                    whiteSpace: "normal !important", // überschreibt noWrap aus internen Komponenten
                                    wordBreak: "keep-all", // kein Umbruch mitten im Wort
                                    overflowWrap: "normal", // nur an Leerzeichen/Zeichen wie Komma
                                    hyphens: "none", // kein automatisches "-"
                                    textOverflow: "clip",
                                    overflow: "visible",
                                    padding: "4px 6px"
                                },

                                // Speziell: falls DataTable Typography mit noWrap nutzt
                                "& .MuiTypography-noWrap": {
                                    whiteSpace: "normal !important",
                                    textOverflow: "clip",
                                    overflow: "visible"
                                },

                                // Falls DataTable die Zell-Children als direkte span/div rendert:
                                "& tbody td>span, & tbody td>div": {
                                    display: "block" // erleichtert den Zeilenumbruch im fixed-Layout
                                },
                                // Erste Spalte (Grade)
                                "& th:nth-of-type(1), & td:nth-of-type(1)": {
                                    minWidth: "95px",
                                    maxWidth: "120px",
                                    width: "100px"
                                },
                                // Zweite Spalte (Flute)
                                "& th:nth-of-type(2), & td:nth-of-type(2)": {
                                    minWidth: "68px",
                                    maxWidth: "80px",
                                    width: "70px"
                                },
                                // AD / o. liner
                                "& th:nth-of-type(3), & td:nth-of-type(3)": {
                                    minWidth: "85px",
                                    maxWidth: "100px",
                                    width: "95px"
                                },
                                // W1 flute
                                "& th:nth-of-type(4), & td:nth-of-type(4)": {
                                    minWidth: "100px",
                                    maxWidth: "115px",
                                    width: "110px"
                                },
                                // ZD 1 / liner
                                "& th:nth-of-type(5), & td:nth-of-type(5)": {
                                    minWidth: "70px",
                                    maxWidth: "110px",
                                    width: "90px"
                                },
                                // W2 flute
                                "& th:nth-of-type(6), & td:nth-of-type(6)": {
                                    minWidth: "80px",
                                    maxWidth: "100px",
                                    width: "90px"
                                },
                                // ZD 2 / liner
                                "& th:nth-of-type(7), & td:nth-of-type(7)": {
                                    minWidth: "80px",
                                    maxWidth: "100px",
                                    width: "90px"
                                },
                                // W3 flute
                                "& th:nth-of-type(8), & td:nth-of-type(8)": {
                                    minWidth: "80px",
                                    maxWidth: "100px",
                                    width: "90px"
                                },
                                // ID i. liner
                                "& th:nth-of-type(9), & td:nth-of-type(9)": {
                                    minWidth: "65px",
                                    maxWidth: "100px",
                                    width: "75px"
                                },
                                // Gewicht
                                "& th:nth-of-type(10), & td:nth-of-type(10)": {
                                    minWidth: "95px",
                                    maxWidth: "120px",
                                    width: "115px"
                                },
                                // ECT Edge Crush
                                "& th:nth-of-type(11), & td:nth-of-type(11)": {
                                    minWidth: "95px",
                                    maxWidth: "120px",
                                    width: "100px"
                                },
                                // NF
                                "& th:nth-of-type(12), & td:nth-of-type(12)": {
                                    minWidth: "55px",
                                    maxWidth: "60px",
                                    width: "55px"
                                },
                                // Standard Plants
                                "& th:nth-of-type(13), & td:nth-of-type(13)": {
                                    minWidth: "110px",
                                    maxWidth: "180px",
                                    width: "120px"
                                },
                                // Special Plants
                                "& th:nth-of-type(14), & td:nth-of-type(14)": {
                                    minWidth: "110px",
                                    maxWidth: "180px",
                                    width: "120px"
                                }
                            }}
                    />
                </LayoutItem>
            </Layout>
    );
};
